import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { handleMutationError } from '@/lib/error-handling';

export interface PaymentRecord {
  id: string;
  paymentRunId: string;
  amount: number;
  allocations?: Array<{
    indicator: string;
    amount: number;
    scheduleItemId?: string;
  }>;
  /** Legacy single-allocation response during backend rollout. */
  allocatedTo?: string;
  reference?: string;
  description?: string;
  status: string;
  method: string;
  processedBy?: string;
  notes?: string;
  receivedDate?: string;
  createdAt: string;
}

export interface OutstandingSummary {
  principal: number;
  interest: number;
  fees: number;
  totalOutstanding: number;
  totalPaid: number;
  originalPrincipal: number;
  nextDueDate: string | null;
  nextDueAmount: number;
  overdueCount: number;
  overdueAmount: number;
  scheduleProgress: {
    total: number;
    paid: number;
    partial: number;
    overdue: number;
    pending: number;
  };
}

export interface RecordPaymentInput {
  loanId: string;
  amount: number;
  method: string;
  reference?: string;
  notes?: string;
  receivedDate?: string;
  processedBy?: string;
}

// Custom endpoint /payments — keep manual
async function fetchPaymentHistory(loanId: string): Promise<PaymentRecord[]> {
  const { data } = await api.get<PaymentRecord[]>(`/loans/${loanId}/payments`);
  return data;
}

export function usePaymentHistory(loanId: string) {
  return useQuery({
    queryKey: ['loans', loanId, 'payments'],
    queryFn: () => fetchPaymentHistory(loanId),
    enabled: !!loanId,
    staleTime: 5 * 60 * 1000,
  });
}

// Custom endpoint /payments/outstanding — keep manual
async function fetchOutstanding(loanId: string): Promise<OutstandingSummary> {
  const { data } = await api.get<OutstandingSummary>(`/loans/${loanId}/payments/outstanding`);
  return data;
}

export function useOutstanding(loanId: string) {
  return useQuery({
    queryKey: ['loans', loanId, 'outstanding'],
    queryFn: () => fetchOutstanding(loanId),
    enabled: !!loanId,
    staleTime: 5 * 60 * 1000,
  });
}

// Custom endpoint — keep manual
async function recordPayment(input: RecordPaymentInput) {
  const { loanId, ...body } = input;
  const { data } = await api.post(`/loans/${loanId}/payments`, body);
  return data;
}

export function useRecordPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: recordPayment,
    onMutate: async vars => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await qc.cancelQueries({ queryKey: ['loans', vars.loanId, 'outstanding'] });
      const previousOutstanding = qc.getQueryData<OutstandingSummary>([
        'loans',
        vars.loanId,
        'outstanding',
      ]);

      // Optimistically reduce outstanding by the payment amount
      if (previousOutstanding) {
        qc.setQueryData<OutstandingSummary>(['loans', vars.loanId, 'outstanding'], {
          ...previousOutstanding,
          totalOutstanding: Math.max(0, previousOutstanding.totalOutstanding - vars.amount),
          totalPaid: previousOutstanding.totalPaid + vars.amount,
        });
      }
      return { previousOutstanding };
    },
    onError: (err, vars, context) => {
      // Roll back to previous value on error
      if (context?.previousOutstanding) {
        qc.setQueryData(['loans', vars.loanId, 'outstanding'], context.previousOutstanding);
      }
      handleMutationError(err, 'Failed to record payment');
    },
    onSettled: (_data, _err, vars) => {
      // Targeted: payments don't change the board or list status — only refresh
      // the specific loan sub-queries that are affected
      qc.invalidateQueries({ queryKey: ['loans', vars.loanId, 'payments'], exact: true });
      qc.invalidateQueries({ queryKey: ['loans', vars.loanId, 'outstanding'], exact: true });
      qc.invalidateQueries({ queryKey: ['loans', vars.loanId], exact: true });
      qc.invalidateQueries({ queryKey: ['loans', vars.loanId, 'activity'], exact: true });
      qc.invalidateQueries({ queryKey: ['ledger'] });
    },
  });
}
